import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

/**
 * Simplified Local Wallet Context
 * No longer uses Phantom - purely local storage based wallets
 */

const SolanaWalletContext = createContext(null);

export const useSolanaWallet = () => {
  const context = useContext(SolanaWalletContext);
  if (!context) {
    throw new Error('useSolanaWallet must be used within a SolanaWalletProvider');
  }
  return context;
};

export const SolanaWalletProvider = ({ children }) => {
  const [connected, setConnected] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [wallet, setWallet] = useState(null);
  const [publicKey, setPublicKey] = useState(null);

  // Check for existing local wallet on mount
  useEffect(() => {
    const storedWallet = localStorage.getItem('burner_wallet');
    if (storedWallet) {
      try {
        const parsed = JSON.parse(storedWallet);
        setWallet(parsed);
        setPublicKey(parsed.playerId);
        setConnected(true);
      } catch (e) {
        console.error('Failed to parse stored wallet:', e);
      }
    }
  }, []);

  // Connect creates a local wallet if none exists
  const connect = useCallback(async () => {
    setConnecting(true);
    
    try {
      // Check if wallet exists
      let existingWallet = localStorage.getItem('burner_wallet');
      if (existingWallet) {
        const parsed = JSON.parse(existingWallet);
        setWallet(parsed);
        setPublicKey(parsed.playerId);
        setConnected(true);
        return parsed;
      }

      // Create new wallet
      const array = new Uint8Array(32);
      crypto.getRandomValues(array);
      const privateKey = Array.from(array).map(b => b.toString(16).padStart(2, '0')).join('');
      
      const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
      let playerId = '';
      for (let i = 0; i < 8; i++) {
        playerId += chars.charAt(Math.floor(Math.random() * chars.length));
      }
      
      const newWallet = {
        playerId,
        privateKey,
        publicKey: playerId,
        createdAt: Date.now()
      };
      
      localStorage.setItem('burner_wallet', JSON.stringify(newWallet));
      localStorage.setItem('player_id', playerId);
      
      setWallet(newWallet);
      setPublicKey(playerId);
      setConnected(true);
      
      return newWallet;
    } finally {
      setConnecting(false);
    }
  }, []);

  // Disconnect clears the wallet
  const disconnect = useCallback(() => {
    localStorage.removeItem('burner_wallet');
    localStorage.removeItem('player_id');
    setWallet(null);
    setPublicKey(null);
    setConnected(false);
  }, []);

  const value = {
    connected,
    connecting,
    wallet,
    publicKey,
    connect,
    disconnect,
    // For compatibility
    signTransaction: () => Promise.reject(new Error('Local wallets cannot sign transactions')),
    signAllTransactions: () => Promise.reject(new Error('Local wallets cannot sign transactions')),
  };

  return (
    <SolanaWalletContext.Provider value={value}>
      {children}
    </SolanaWalletContext.Provider>
  );
};

export default SolanaWalletProvider;
